/**
 * The refusal surface (docs/workspace-roles/design.md §9.1, rule R1): outside
 * banking a control renders, the API refuses, and this module names the
 * missing permission. The API refuses in the payload with HTTP 200, so Apollo
 * fires onCompleted, never onError; a call site reads `data.<op>.error`
 * through `payloadError` and stops, and the link in apolloClient.ts writes
 * the copy.
 *
 * Nothing here decides anything: the explanation is built from
 * OPERATION_AUTHZ and the member's grants, after the API already refused.
 */
import {
  getOperationAST,
  type DocumentNode,
  type FieldNode,
  type ValueNode,
} from 'graphql'
import {
  CATALOG_BY_KEY,
  OPERATION_AUTHZ,
  requiredKeysFor,
  type NoKeyReason,
  type OperationAuthz,
  type PermissionKey,
} from '@nickeltechnology/types'
import { operationNameOf, type EffectiveGrants } from './permissions'

export const UNAUTHORIZED_TYPE = 'UnauthorizedUserError'
/**
 * Its stock message (apps/api/src/lib/errors.ts). Many `error { … }`
 * selections omit `type`, so a refusal is also recognised by this text.
 */
export const UNAUTHORIZED_MESSAGE =
  'The logged in user is not allowed to perform that action.'

export type PayloadError = {
  type?: string | null
  message?: string | null
}

export function refusalToastId(operation: string | DocumentNode): string {
  const name =
    typeof operation === 'string'
      ? operation
      : getOperationAST(operation)?.name?.value
  return `refusal:${name || 'anonymous'}`
}

export function isUnauthorizedError(error: PayloadError | null | undefined) {
  if (!error) return false
  if (error.type === UNAUTHORIZED_TYPE) return true
  return !error.type && error.message === UNAUTHORIZED_MESSAGE
}

/**
 * The payload's error, or null. Read `data.<op>.error` through this, never
 * bare: a success with `error: {}` reaches the client as
 * `{ type: null, message: null }`, which is truthy (a saved phone number once
 * came back as "Unknown error"). Every API error carries a `type` and a
 * `message`, so one with neither is no error and a refusal is never hidden.
 */
export function payloadError<T extends PayloadError>(
  error: T | null | undefined,
): T | null {
  return error && (error.type || error.message) ? error : null
}

export function findRefusal(
  data: Record<string, unknown> | null | undefined,
): { field: string; error: PayloadError } | null {
  if (!data) return null
  for (const [field, payload] of Object.entries(data)) {
    if (field === '__typename' || !payload || typeof payload !== 'object') {
      continue
    }
    const error = (payload as { error?: PayloadError | null }).error
    if (isUnauthorizedError(error)) return { field, error: error! }
  }
  return null
}

export type BatchResult = {
  succeeded: number
  failures: PayloadError[]
  refusedOnly: boolean
}

export function batchResult(
  errors: readonly (PayloadError | null | undefined)[],
): BatchResult {
  const failures = errors.flatMap((error) => {
    const failure = payloadError(error)
    return failure ? [failure] : []
  })
  return {
    succeeded: errors.length - failures.length,
    failures,
    refusedOnly: failures.length > 0 && failures.every(isUnauthorizedError),
  }
}

export type Refusal =
  | {
      kind: 'missing-keys'
      operation: string
      missing: readonly PermissionKey[]
      message: string
    }
  | {
      kind: 'ungated'
      operation: string
      reason: NoKeyReason
      message: string
    }
  | {
      kind: 'unexplained'
      operation: string
      message: string
      unmapped: boolean
    }

const ASK = 'Ask an administrator.'

function quoteLabels(keys: readonly PermissionKey[]): string {
  const labels = keys.map((key) => `"${CATALOG_BY_KEY.get(key)?.label ?? key}"`)
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

export function missingPermissionMessage(
  missing: readonly PermissionKey[],
  action = 'do this',
): string {
  if (missing.length === 0) {
    return `You do not have the relevant permissions to ${action}. ${ASK}`
  }
  return `You need the ${quoteLabels(missing)} permission${
    missing.length > 1 ? 's' : ''
  } to ${action}. ${ASK}`
}

export function explainRefusal({
  document,
  variables,
  grants,
  apiMessage,
}: {
  document: DocumentNode
  variables?: Record<string, unknown> | null
  grants: EffectiveGrants
  apiMessage?: string | null
}): Refusal {
  const fallback = apiMessage || UNAUTHORIZED_MESSAGE
  let operation: string
  try {
    operation = operationNameOf(document)
  } catch {
    return {
      kind: 'unexplained',
      operation: 'unknown',
      message: fallback,
      unmapped: true,
    }
  }
  const authz: OperationAuthz | undefined = OPERATION_AUTHZ[operation as never]
  if (!authz) {
    return { kind: 'unexplained', operation, message: fallback, unmapped: true }
  }
  if ('ungated' in authz) {
    const ownReason =
      apiMessage && apiMessage !== UNAUTHORIZED_MESSAGE ? apiMessage : null
    if (authz.ungated === 'legacy-tier') {
      return {
        kind: 'ungated',
        operation,
        reason: authz.ungated,
        message:
          ownReason ?? 'You do not have the relevant permissions to do this.',
      }
    }
    if (authz.ungated === 'staff') {
      return {
        kind: 'ungated',
        operation,
        reason: authz.ungated,
        message: ownReason ?? 'Only Nickel staff can do this.',
      }
    }
  }
  const sent = Object.keys(authz.argumentRequires ?? {}).filter((path) =>
    argumentCarries(document, variables ?? {}, path),
  )
  const required = requiredKeysFor(authz, sent)
  const missing = required.filter((key) => !grants.has(key))
  if (missing.length === 0) {
    return {
      kind: 'unexplained',
      operation,
      message: fallback,
      unmapped: false,
    }
  }
  return {
    kind: 'missing-keys',
    operation,
    missing,
    message: missingPermissionMessage(missing),
  }
}

export function explainReadFailure({
  document,
  variables,
  grants,
  fetchError,
  payloadError,
}: {
  document: DocumentNode
  variables?: Record<string, unknown> | null
  grants: EffectiveGrants
  fetchError?: { message: string } | null
  payloadError?: PayloadError | null
}): { message: string; refused: boolean } | null {
  if (fetchError) return { message: fetchError.message, refused: false }
  if (!payloadError) return null
  if (!isUnauthorizedError(payloadError)) {
    return payloadError.message
      ? { message: payloadError.message, refused: false }
      : null
  }
  return {
    message: explainRefusal({
      document,
      variables,
      grants,
      apiMessage: payloadError.message,
    }).message,
    refused: true,
  }
}

export function argumentCarries(
  document: DocumentNode,
  variables: Record<string, unknown>,
  path: string,
): boolean {
  const segments = path
    .split('.')
    .map((segment) =>
      segment.endsWith('[]')
        ? { name: segment.slice(0, -2), list: true }
        : { name: segment, list: false },
    )
  const [head, ...rest] = segments
  const field = rootField(document)
  const argument = field?.arguments?.find((a) => a.name.value === head.name)
  if (!argument) return false
  return walk(valueOf(argument.value, variables), head.list, rest)
}

type Segment = { name: string; list: boolean }

function walk(
  value: unknown,
  list: boolean,
  rest: readonly Segment[],
): boolean {
  if (list) {
    return Array.isArray(value) && value.some((item) => walk(item, false, rest))
  }
  if (rest.length === 0) return meaningful(value)
  const object = asObject(value)
  if (!object) return false
  const [next, ...after] = rest
  return walk(object[next.name], next.list, after)
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  return null
}

function rootField(document: DocumentNode): FieldNode | undefined {
  for (const definition of document.definitions) {
    if (definition.kind !== 'OperationDefinition') continue
    for (const selection of definition.selectionSet.selections) {
      if (selection.kind === 'Field' && selection.name.value !== '__typename') {
        return selection
      }
    }
  }
  return undefined
}

function valueOf(node: ValueNode, variables: Record<string, unknown>): unknown {
  switch (node.kind) {
    case 'Variable':
      return variables[node.name.value]
    case 'NullValue':
      return null
    case 'IntValue':
    case 'FloatValue':
      return Number(node.value)
    case 'StringValue':
    case 'EnumValue':
      return node.value
    case 'BooleanValue':
      return node.value
    case 'ListValue':
      return node.values.map((value) => valueOf(value, variables))
    case 'ObjectValue':
      return Object.fromEntries(
        node.fields.map((f) => [f.name.value, valueOf(f.value, variables)]),
      )
    default:
      return undefined
  }
}

function meaningful(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return false
  if (Array.isArray(value)) return value.length > 0
  return true
}
